/**
 * The compact game row: what History, a player's own profile and anybody's Recent games all list. Pure, so the words
 * and the numbers are testable without a DOM; the row itself is built in screens.ts (`gameRow`).
 *
 * Blockari's `shell/GameRow.tsx` (c258375): a ~60 px row with a 3 px colour bar, "vs <opponent>" and the net on line
 * one, and a muted caption on line two ("6,680 – 4,360 · 10 stake · Created · 18h": the owner's score first, bold
 * when they won). A settled game shows its net on the right ("+10" / "−10" with the coin; "Free" or "Ticket" when no
 * $CHAIN moved); every other state shows its own word there.
 *
 * What 21 Wild adds to the words: a forfeited round (the abandoned-seat sweep writes a null score) reads "forfeit" in
 * the caption where its score would be, a tie reads "Tied", and a voided challenge reads "Voided · Refunded".
 */

import type { ChallengeCard, GameRow, RankView } from '@/net/api';
import { gameStatus } from '@/ui/profile-games';

/** `4 min ago`, `2 h ago`, `3 d ago`, then a short date: Blockari's `timeAgo`, so one event reads the same on both titles. */
export function timeAgo(iso: string, now = Date.now()): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "18 h ago" as "18h" (and "5 min ago" as "5m"): the caption's phone form. */
export function shortAgo(iso: string, now = Date.now()): string {
  return timeAgo(iso, now)
    .replace(/ min ago$/, 'm')
    .replace(/ h ago$/, 'h')
    .replace(/ d ago$/, 'd')
    .replace(/^just now$/, 'now');
}

/** `+1,250`, `−300`, `0`: a net never shows a sign on zero, and the minus is the typographic one. */
export function signedNet(n: number): string {
  return n === 0 ? '0' : `${n < 0 ? '−' : '+'}${Math.abs(n).toLocaleString('en-US')}`;
}

const num = (n: number): string => n.toLocaleString('en-US');

/** The colour bar's state: won/lost are the money states; refund, open, pending and quiet the rest. */
export type RowTone = 'won' | 'lost' | 'refund' | 'open' | 'pending' | 'quiet';

/** A seat's score in the caption: a number, `'forfeit'` (never played), or null (still playing: "…"). */
export type RowScore = number | 'forfeit' | null;

export interface GameRowData {
  tone: RowTone;
  /** Settled (won/lost): the net, shown signed on the right. Absent: `word` is shown there. */
  net?: number | undefined;
  /** The right-hand text of an unsettled row ("Waiting", "Expired", "Voided · Refunded"). */
  word: string;
  /** The state said in full, for the screen reader and the label ("Won by forfeit", "Lost, timed out", "Voided, refunded"). */
  status: string;
  /** The other side, from the PROFILE OWNER's point of view. Absent: `title` stands in. `rank` null: no rank on record. */
  opponent?: { name: string; rank: RankView | null; profile?: string | undefined } | null | undefined;
  /** Line one when there is no opponent ("Open challenge", "Not taken"). */
  title?: string | undefined;
  /** The owner's score and the other side's; `theirs` undefined: no second score to show. */
  mine?: RowScore | undefined;
  theirs?: RowScore | undefined;
  /** Who took it, for the bold on the winning score. */
  winner?: 'me' | 'them' | null | undefined;
  /** The entry fee. */
  stake: number;
  /** Its stake went back to the player in $CHAIN. */
  refunded?: boolean | undefined;
  role?: 'creator' | 'taker' | null | undefined;
  at: string;
  /** Anything else for the caption's end (an open challenge's code). */
  extra?: string | undefined;
  /** The whole row opens this challenge; absent: a plain row (the opponent's name may link to their profile). */
  id?: string | undefined;
}

/**
 * A settled game whose net is 0 moved no $CHAIN: it was a free game (no stake) or a ticket entry (the house funded the
 * stake, so a loss costs nothing). The row says that word instead of "0".
 */
export function zeroNetWord(d: Pick<GameRowData, 'net' | 'stake'>): 'Ticket' | 'Free' | null {
  return d.net === 0 ? (d.stake > 0 ? 'Ticket' : 'Free') : null;
}

/** What one chunk of the caption is: the two scores, or a plain word. Chunks are joined with a middle dot. */
export type CaptionChunk =
  | { kind: 'score'; mine: string; theirs: string | null; mineWon: boolean; theirsWon: boolean }
  | { kind: 'text'; text: string };

const scoreText = (s: RowScore): string => (s === 'forfeit' ? 'forfeit' : s === null ? '…' : num(s));

/** The caption's chunks, in order: scores, stake, Created/Taken, the extra, then the time (added by the caller). */
export function captionChunks(d: GameRowData): CaptionChunk[] {
  const out: CaptionChunk[] = [];
  if (d.mine !== undefined && d.mine !== null) {
    out.push({
      kind: 'score',
      mine: scoreText(d.mine),
      theirs: d.theirs === undefined ? null : scoreText(d.theirs),
      mineWon: d.winner === 'me',
      theirsWon: d.winner === 'them',
    });
  }
  out.push({ kind: 'text', text: d.stake > 0 ? `${num(d.stake)} stake${d.refunded ? ' refunded' : ''}` : 'No stake' });
  if (d.role) out.push({ kind: 'text', text: d.role === 'creator' ? 'Created' : 'Taken' });
  if (d.extra) out.push({ kind: 'text', text: d.extra });
  return out;
}

/** The whole caption as one string ("6,680 – 4,360 · 10 stake · Created · 18h"): the test seam and the plain-text form. */
export function captionText(d: GameRowData, now = Date.now(), short = true): string {
  const parts = captionChunks(d).map((c) =>
    c.kind === 'text' ? c.text : c.theirs === null ? c.mine : `${c.mine} – ${c.theirs}`,
  );
  parts.push(short ? shortAgo(d.at, now) : timeAgo(d.at, now));
  return parts.join(' · ');
}

/** The right-hand side of the first line: a coloured net, the muted "Free"/"Ticket", or the state's own word. */
export function rightSide(d: GameRowData): { kind: 'net'; text: string; tone: 'pos' | 'neg' } | { kind: 'word'; text: string } {
  if (d.net === undefined) return { kind: 'word', text: d.word };
  const zero = zeroNetWord(d);
  if (zero) return { kind: 'word', text: zero };
  return { kind: 'net', text: signedNet(d.net), tone: d.tone === 'won' ? 'pos' : 'neg' };
}

/** The screen-reader summary of the row: "Won 10 $CHAIN against grace, 6,680 to 4,360, 10 stake, created, 18 h ago". */
export function rowLabel(d: GameRowData, now = Date.now()): string {
  const settled = d.net !== undefined;
  const zero = zeroNetWord(d);
  const who = d.opponent ? ` against ${d.opponent.name}` : '';
  const parts = [
    settled
      ? `${d.status} ${zero ? `(${zero.toLowerCase()} entry, no $CHAIN moved)` : `${num(Math.abs(d.net ?? 0))} $CHAIN`}${who}`
      : `${d.status}${d.opponent ? who : d.title ? `, ${d.title}` : ''}`,
  ];
  if (d.mine !== undefined && d.mine !== null) {
    const mine = d.mine === 'forfeit' ? 'no round' : num(d.mine);
    if (d.theirs === undefined) parts.push(`score ${mine}`);
    else
      parts.push(
        `${mine} to ${d.theirs === null ? 'opponent still playing' : d.theirs === 'forfeit' ? 'no round' : num(d.theirs)}`,
      );
  }
  parts.push(d.stake > 0 ? `stake ${num(d.stake)}${d.refunded ? ' refunded' : ''}` : 'no stake');
  if (d.role) parts.push(d.role === 'creator' ? 'created' : 'taken');
  parts.push(timeAgo(d.at, now));
  return parts.join(', ');
}

/* ------------------------------------------------------------ History -- */

/**
 * How the challenge stands, in the kit's words.
 *
 * Blockari's `statusLabel`, with the one state it lacks: 21 Wild can tie. Order matters: a finished challenge is
 * finished whatever its deadline said, and a challenge waiting on YOU is that before it is anything else.
 */
export function matchStatus(row: ChallengeCard): { text: string; cls: string } {
  // Voided (the server lost a started round with a restart): both stakes were refunded, nobody won or lost.
  if (row.voided) return { text: 'VOIDED · REFUNDED', cls: '' };
  // A round that was never played (the server forfeits an abandoned seat) is a loss that says so, instead of a
  // mysterious score; its winner is told the other side never turned up.
  if (row.outcome === 'won') return row.theirForfeited ? { text: 'WON · FORFEIT', cls: 'won' } : { text: 'WON', cls: 'won' };
  if (row.outcome === 'lost') return row.myForfeited ? { text: 'LOST · TIMED OUT', cls: 'lost' } : { text: 'LOST', cls: 'lost' };
  if (row.outcome === 'draw') return { text: 'TIED', cls: '' };
  if (row.expired) return { text: 'EXPIRED', cls: '' };
  if (row.myScore === null) return { text: 'YOUR TURN', cls: 'pending' };
  // Both seats bought and their round not in: somebody is playing this deck right now. `opponent` cannot answer it:
  // that comes from their result and stays null for exactly as long as the match is still live.
  if (row.taken || row.opponent !== null) return { text: 'OPPONENT PLAYING', cls: 'taken' };
  return { text: 'WAITING', cls: 'open' };
}

const isNamed = (p: { name: string } | null): p is { name: string } => p !== null && !/^anonymous$/i.test(p.name.trim());

/** What an unsettled row says on the right (Blockari's `STATE_WORD`), from `matchStatus`'s text. */
const STATE_WORD: Record<string, string> = {
  'YOUR TURN': 'Your turn',
  'OPPONENT PLAYING': 'Opponent playing',
  WAITING: 'Waiting',
  EXPIRED: 'Expired',
  TIED: 'Tied',
};

/**
 * A challenge of mine as the compact row's data: "vs <opponent>", the net from my ledger, the caption. `net` is the
 * server's ledger net; a server that predates it sends none, and the row reckons payout - stake. `code: false` leaves an
 * open challenge's code out of the caption (the profile's narrow Recent challenges list, where it would push the time off).
 */
export function matchRowData(c: ChallengeCard, options: { code?: boolean } = {}): GameRowData {
  const status = matchStatus(c);
  const opp = c.opponent;
  const decided = c.outcome === 'won' || c.outcome === 'lost';
  const won = c.outcome === 'won';
  const mine: RowScore | undefined = c.myForfeited ? 'forfeit' : c.myScore;
  const seen = (s: number | null): RowScore | undefined => (c.theirForfeited ? 'forfeit' : s);
  const extra = options.code === false ? undefined : (c.code ?? undefined);
  const base = {
    opponent: opp ? { name: opp.name, rank: isNamed(opp) ? opp.rank : null } : null,
    // A private challenge says so on line one (the old card's indigo chip), where there is room; the caption keeps its code.
    title: c.voided ? 'Voided' : c.expired ? 'Not taken' : c.isPrivate ? 'Private challenge' : c.taken ? 'Challenge' : 'Open challenge',
    mine: mine ?? undefined,
    stake: c.entryFee,
    role: c.role,
    at: c.activityAt,
    id: c.id,
  };
  if (c.voided) {
    // Both stakes went back: a refund, whatever the scores were (a voided challenge has no winner).
    return {
      ...base,
      tone: 'refund',
      word: 'Voided · Refunded',
      status: 'Voided, refunded',
      refunded: c.entryFee > 0,
      ...(opp && c.theirScore !== null ? { theirs: seen(c.theirScore) } : {}),
    };
  }
  if (decided) {
    return {
      ...base,
      tone: won ? 'won' : 'lost',
      status: c.myForfeited ? 'Lost, timed out' : c.theirForfeited ? 'Won by forfeit' : won ? 'Won' : 'Lost',
      word: won ? 'Won' : 'Lost',
      net: c.net ?? (won ? (c.payout ?? 0) - c.entryFee : -c.entryFee),
      theirs: seen(c.theirScore) ?? null,
      winner: won ? 'me' : 'them',
    };
  }
  if (status.text === 'EXPIRED') {
    return { ...base, tone: c.entryFee > 0 ? 'refund' : 'quiet', word: 'Expired', status: 'Expired', refunded: c.entryFee > 0 };
  }
  if (status.text === 'TIED') {
    return {
      ...base,
      tone: 'quiet',
      word: 'Tied',
      status: 'Tied',
      theirs: seen(c.theirScore) ?? null,
    };
  }
  const word = STATE_WORD[status.text] ?? 'Waiting';
  return {
    ...base,
    tone: word === 'Your turn' ? 'pending' : 'open',
    word,
    status: word,
    // Somebody is playing it: "5,000 – …"
    ...(word === 'Opponent playing' ? { theirs: null } : {}),
    ...(extra ? { extra } : {}),
  };
}

/* ------------------------------------------------------------ Profile -- */

/**
 * What a visitor may see: settled games only. The public endpoint never sends an open, waiting or in-play challenge to
 * anyone but its owner (profile-games.ts: the query and `toRow` both refuse it); this is the third lock, so a row that
 * slipped through is never drawn. Blockari's `publicRows`.
 */
export function publicRows<T extends Pick<GameRow, 'result'>>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.result !== 'open' && r.result !== 'playing');
}

/** A game of this profile as the compact row's data: the opponent is the OTHER side of the owner's game. */
export function gameRowData(r: GameRow): GameRowData {
  const opp = r.opponent;
  const status = gameStatus(r);
  const base = {
    opponent: opp ? { name: opp.username, rank: opp.rank, profile: opp.username } : null,
    stake: r.wager,
    role: r.mode === 'challenge' ? r.role : null,
    at: r.at,
  };
  switch (r.result) {
    case 'win':
    case 'loss': {
      const won = r.result === 'win';
      // The row has no forfeit flag: a null score on a decided game is the seat that never played.
      const mineNull = r.score === null;
      const theirNull = opp !== null && opp.score === null;
      return {
        ...base,
        tone: won ? 'won' : 'lost',
        word: won ? 'Won' : 'Lost',
        status: status.text === 'WON · FORFEIT' ? 'Won by forfeit' : status.text === 'LOST · TIMED OUT' ? 'Lost, timed out' : won ? 'Won' : 'Lost',
        net: r.net,
        mine: mineNull ? 'forfeit' : r.score,
        ...(opp ? { theirs: theirNull ? ('forfeit' as const) : opp.score } : {}),
        winner: won ? 'me' : 'them',
      };
    }
    case 'voided':
      return {
        ...base,
        tone: 'refund',
        word: 'Voided · Refunded',
        status: 'Voided, refunded',
        refunded: r.refunded && r.wager > 0,
        title: 'Voided',
        mine: r.score,
        ...(opp && opp.score !== null ? { theirs: opp.score } : {}),
      };
    case 'expired':
      return {
        ...base,
        tone: r.refunded ? 'refund' : 'quiet',
        word: 'Expired',
        status: 'Expired',
        refunded: r.refunded && r.wager > 0,
        title: 'Not taken',
        mine: r.score,
      };
    case 'open':
      return { ...base, tone: 'open', word: 'Waiting', status: 'Waiting', title: 'Open challenge', mine: r.score };
    default:
      return { ...base, tone: 'pending', word: 'In play', status: 'In play', title: 'Challenge', mine: r.score };
  }
}
